import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  anfrageregel,
  type Anfrageregel,
  besetzungskandidaten,
  besetzungslage,
  einsatzAnfragen,
  einsatzAntwort,
  type Besetzungszeile,
  type Kandidatenzeile,
  type Verlaufszeile,
} from '../api/ressourcen';
import {
  BEFUNDNAME,
  EINHEITSNAME,
  STUFENNAME,
  TARIFNAME,
  tabelle as ladeHonorartabelle,
  vorschau as ladeVorschau,
  type Grundzeile,
  type Honorarvorschau,
  type Leistungsartzeile,
} from '../api/honorare';
import { alsEuro, alsEurosatz } from '../betrag';
import { ApiFehler } from '../api/fehler';
import { markenklasse } from '../marken';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Planung } from '../komponenten/Planung';
import { Segmente } from '../komponenten/Segmente';
import { Feldraster, Feldzeile } from '../komponenten/Akte';

/**
 * Die nachgelagerte Besetzung (RES-49, RES-33, RES-50).
 *
 * ============================================================================
 * WARUM DIESE LISTE NEBEN DER ENGPASSLISTE STEHT UND NICHT DARIN
 * ============================================================================
 *
 * RES-49: „Ein Termin kann geplant, veröffentlicht und gebucht werden, BEVOR
 * eine Zusage vorliegt." Ein Termin ohne Trainer ist hier also kein Fehler,
 * sondern der Normalzustand — bis zur Besetzungsfrist.
 *
 * Die Engpassliste nach RES-24 beantwortet etwas anderes: „welchem verkauften
 * Termin fehlt eine Ressource". Beides in eine Liste zu legen hieße, den
 * Normalfall als Rückstand zu führen — und nach zwei Wochen liest ihn
 * niemand mehr.
 *
 * ============================================================================
 * DIE FRIST IST DIE ORDNUNG UND DIE AMPEL, NICHT DER TERMIN
 * ============================================================================
 *
 * Ein Kurs in drei Tagen, dessen Frist erst morgen abläuft, ist ruhiger als
 * einer in vier Wochen, dessen Frist gestern verstrichen ist: Beim zweiten
 * sind die drei automatischen Versuche verbraucht (RES-33), und ab da
 * entscheidet ein Mensch (RES-50).
 *
 * ============================================================================
 * ZWEI LISTEN UNTER EINER ZEILE
 * ============================================================================
 *
 * „Wer kann?" zeigt die Freien — und daneben, wen wir schon gefragt haben.
 * Beides ist nötig, weil das eine das andere ausschließt: Eine laufende
 * Anfrage blockiert die Ressource (RES-22), also steht der gerade Angefragte
 * NICHT unter den Kandidaten. Ohne den Verlauf stünde in der Zeile „Anfrage
 * läuft" und nirgends, von wem.
 *
 * ============================================================================
 * WAS DIE MASKE NICHT TUT
 * ============================================================================
 *
 * Sie sagt nichts ab. RES-50 nennt vier Wege — weiterer Dienstleister,
 * interne Besetzung, Verlegung, Absage — und drei davon liegen außerhalb.
 * Hier steht der erste; die anderen drei stehen als Satz da, damit niemand
 * meint, es gäbe nur diesen.
 *
 * ============================================================================
 * SEITENTYP PLANUNG (ÄA-41, Auftrag #19)
 * ============================================================================
 *
 * Oben die Werkzeugleiste — Termine ab einem Tag, Standort, Ansicht —, darunter
 * die offenen Besetzungen in voller Breite und, sobald eine Zeile aufgeklappt
 * ist, wer den Bedarf decken kann und wen wir schon gefragt haben. Beides sind
 * breite Tabellen mit Handlungen je Zeile; sie gehören in die Fläche.
 *
 * Die Seitenleiste trägt den Stand und das GEWÄHLTE: wie viele Fristen
 * verstrichen sind, den aufgeklappten Bedarf mit seiner Frist und — mit
 * Honorarrecht — das Honorar zum gewählten Kandidaten (RES-70). Die
 * Werkzeugleiste filtert nur, was geladen ist: `besetzungslage` kennt keinen
 * Zeitraum und keinen Standort, und die Liste ist klein.
 *
 * Fristen und Konflikte stehen in der Achtungsfarbe (`marke--warn`,
 * `streifen--warn`): die verstrichene Frist, der fehlende oder ablaufende
 * Nachweis, die fehlende Vereinbarung. Eine laufende Anfrage ist KEIN
 * Konflikt — sie trägt die Marke des laufenden Zustands.
 */

/** Wie viel Zeit noch bleibt — als Satz und nicht als Zahl mit Vorzeichen. */
function frist(zeile: Besetzungszeile): string {
  const stunden = zeile.stundenBisFrist;
  if (zeile.ueberfaellig) {
    const tage = Math.floor(-stunden / 24);
    return tage >= 1
      ? `Frist seit ${String(tage)} Tagen abgelaufen`
      : `Frist seit ${String(-stunden)} Stunden abgelaufen`;
  }
  const tage = Math.floor(stunden / 24);
  return tage >= 1 ? `noch ${String(tage)} Tage` : `noch ${String(stunden)} Stunden`;
}

function zeitpunkt(wert: string): string {
  const d = new Date(wert);
  return Number.isNaN(d.getTime())
    ? wert
    : d.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

/** Der Tag eines Zeitpunkts als `JJJJ-MM-TT` in Ortszeit — wie ihn das Datumsfeld führt. */
function ortstag(wert: string): string {
  return new Date(wert).toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

function tag(wert: string): string {
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? wert : d.toLocaleDateString('de-DE');
}

/** TAG-14: Warum jemand nicht in Frage kommt — oder nur mit Vorbehalt. */
const BEFUND: Readonly<Record<string, string>> = {
  fehlt: 'trägt die geforderte Qualifikation nicht',
  abgelaufen: 'Nachweis abgelaufen',
  laeuft_ab: 'Nachweis läuft bis zum Termin ab',
};

/** Wie eine Anfrage ausgegangen ist (0063, RES-33). */
const STAND: Readonly<Record<string, string>> = {
  vorlaeufig: 'wartet auf Antwort',
  zugesagt: 'zugesagt',
  bestaetigt: 'bestätigt',
  abgesagt: 'abgesagt',
  verfallen: 'ohne Antwort verfallen',
};

/**
 * Der Honorarentwurf zu EINEM Kandidaten (RES-70).
 *
 * Er steht nur im Zustand der Maske und wird nie gespeichert, solange niemand
 * anfragt: Die Anpassung gehoert zur Anfrage und nicht zum Ansehen.
 */
type Honorarentwurf = {
  readonly ressource: string;
  readonly bezeichnung: string;
  readonly leistungsart: string;
  /** Leer heisst: der Tabellensatz gilt. */
  readonly satz: string;
  readonly grund: string;
  /** Die bestaetigten offenen Zuschlaege mit ihrer Menge. */
  readonly mengen: Readonly<Record<string, string>>;
};

export function Besetzung({
  darfZuordnen,
  darfHonorar,
}: {
  /** RES-18: Ohne `ressource:zuordnen` wird gelesen und nicht gefragt. */
  readonly darfZuordnen: boolean;
  /**
   * RES-73: Ohne `artikel/honorar_sehen` gibt es hier kein Honorar — keine
   * Vorschau, kein Feld, keine Spalte. Die Anfrage selbst bleibt moeglich; sie
   * geht dann ohne Honorar hinaus, und das ist der Zustand, in dem dieses Haus
   * seit Jahren arbeitet.
   */
  readonly darfHonorar: boolean;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Besetzungszeile[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);

  /* Welche Zeile aufgeklappt ist — Termin und Bedarf zusammen, denn ein
     Termin kann mehr als einen offenen Bedarf haben. */
  const [offen, setzeOffen] = useState<{ termin: string; bedarf: string } | null>(null);
  const [kandidaten, setzeKandidaten] = useState<readonly Kandidatenzeile[]>([]);
  const [verlauf, setzeVerlauf] = useState<readonly Verlaufszeile[]>([]);
  const [kandidatenLaden, setzeKandidatenLaden] = useState(false);
  /*
   * EB-DP-14: Der Absagegrund je Verlaufszeile, geschlüsselt nach Belegung.
   * Ein gemeinsamer Grund für alle Zeilen schaltete die Absage bei einer
   * Zeile frei, in die niemand etwas geschrieben hatte.
   */
  const [absagegruende, setzeAbsagegruende] = useState<Readonly<Record<string, string>>>({});
  const absagegrundVon = (belegung: string): string => absagegruende[belegung] ?? '';
  /* ÄW-DP-29, RES-33: Versuche und Frist aus den Einstellungen; null, bis sie da sind. */
  const [regel, setzeRegel] = useState<Anfrageregel | null>(null);
  /*
   * CRM-104: Welche Antwort in der Rückfrage steht. Eine beantwortete Anfrage
   * bleibt, wie sie ist (RES-35) — eingetragen wird erst nach dem Nachfragen.
   */
  const [nachfrage, setzeNachfrage] = useState<{
    readonly zusage: boolean;
    readonly anfrage: Verlaufszeile;
  } | null>(null);

  /* Die Werkzeugleiste (ÄA-41): Tag, Standort, Ansicht. Leer heißt: alle. */
  const [ab, setzeAb] = useState('');
  const [ort, setzeOrt] = useState('');
  const [ansicht, setzeAnsicht] = useState<'alle' | 'verstrichen' | 'ungefragt'>('alle');

  /*
   * DIE HONORARSEITE (RES-70).
   *
   * `leistungsarten` und `gruende` kommen EINMAL und werden behalten: Es sind
   * Stammdaten, die sich im Lauf einer Sitzung nicht aendern, und ein Aufruf je
   * Kandidat waere ein Aufruf je Mausklick.
   */
  const [leistungsarten, setzeLeistungsarten] = useState<readonly Leistungsartzeile[]>([]);
  const [gruende, setzeGruende] = useState<readonly Grundzeile[]>([]);
  const [entwurf, setzeEntwurf] = useState<Honorarentwurf | null>(null);
  const [vorschau, setzeVorschau] = useState<Honorarvorschau | null>(null);
  const [vorschauLaedt, setzeVorschauLaedt] = useState(false);

  /*
   * ÄW-DP-29: Einmal je Aufruf der Maske. Scheitert es, steht der Satz ohne
   * Zahlen — keine feste Vorgabe hier, die eine geänderte Einstellung verdeckte.
   */
  useEffect(() => {
    let gilt = true;
    anfrageregel()
      .then((r) => {
        if (gilt) setzeRegel(r);
      })
      .catch(() => {
        if (gilt) setzeRegel(null);
      });
    return () => {
      gilt = false;
    };
  }, []);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      setzeZeilen(await besetzungslage());
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void holen();
  }, [holen]);

  /*
   * Die Honorarstammdaten holen — nur, wenn das Konto sie sehen darf (RES-73).
   *
   * Ein Aufruf ohne das Recht kaeme als 403 zurueck und wuerde hier eine
   * Fehlermeldung erzeugen, die dem Disponenten nichts sagt: Er hat nichts
   * falsch gemacht, und er vermisst auch nichts.
   */
  useEffect(() => {
    if (!darfHonorar) return;
    void (async () => {
      try {
        const tab = await ladeHonorartabelle();
        setzeLeistungsarten(tab.leistungsarten.filter((l) => l.aktiv));
        setzeGruende(tab.uebersteuerungsgruende.filter((g) => g.aktiv));
      } catch (f) {
        if (!(f instanceof ApiFehler)) throw f;
        /* Die Liste bleibt leer; die Maske zeigt dann kein Honorarfeld. */
      }
    })();
  }, [darfHonorar]);

  const holeKandidaten = useCallback(async (termin: string, bedarf: string) => {
    setzeKandidatenLaden(true);
    try {
      const antwort = await besetzungskandidaten(termin, bedarf);
      setzeKandidaten(antwort.kandidaten);
      setzeVerlauf(antwort.verlauf);
    } catch (f) {
      setzeKandidaten([]);
      setzeVerlauf([]);
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeKandidatenLaden(false);
    }
  }, []);

  /**
   * Jeder Handgriff lädt beides neu — die Liste und die aufgeklappte Zeile.
   *
   * Eine Anfrage ändert beide: Der Angefragte verschwindet aus den Kandidaten
   * (er ist nicht mehr frei) und taucht im Verlauf auf. Nur eines von beiden
   * nachzuziehen hieße, dass die Maske sich selbst widerspricht.
   */
  async function handgriff(tun: () => Promise<unknown>, sagen: string): Promise<void> {
    setzeMeldung(null);
    try {
      await tun();
      await holen();
      if (offen !== null) await holeKandidaten(offen.termin, offen.bedarf);
      setzeMeldung(sagen);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }

  /**
   * Die Antwort aus der Rückfrage (CRM-104). Sie fängt NICHT: Der Fehler
   * steht im Pop-up, das offen bleibt, und nicht ein zweites Mal oben.
   */
  async function antwortEintragen(zusage: boolean, anfrage: Verlaufszeile): Promise<void> {
    setzeMeldung(null);
    if (zusage) {
      await einsatzAntwort(anfrage.belegung, true);
    } else {
      await einsatzAntwort(anfrage.belegung, false, absagegrundVon(anfrage.belegung).trim());
      setzeAbsagegruende((g) => ({ ...g, [anfrage.belegung]: '' }));
    }
    setzeNachfrage(null);
    await holen();
    if (offen !== null) await holeKandidaten(offen.termin, offen.bedarf);
    setzeMeldung(
      zusage
        ? `${anfrage.bezeichnung} hat zugesagt.`
        : `${anfrage.bezeichnung} hat abgesagt; der Nächste wird gefragt.`,
    );
  }

  async function aufklappen(zeile: Besetzungszeile): Promise<void> {
    if (offen?.termin === zeile.termin && offen.bedarf === zeile.bedarf) {
      setzeOffen(null);
      return;
    }
    setzeOffen({ termin: zeile.termin, bedarf: zeile.bedarf });
    setzeAbsagegruende({});
    setzeEntwurf(null);
    setzeVorschau(null);
    await holeKandidaten(zeile.termin, zeile.bedarf);
  }

  /**
   * Die Vorschau zu einem Entwurf holen (RES-70).
   *
   * SIE SCHREIBT NICHTS. Der Disponent sieht, was der Tabellensatz ergibt, und
   * entscheidet dann — ohne diese Zahl waere „Anpassungsmoeglichkeit bei der
   * Anfrage" ein leeres Feld neben einem Knopf.
   *
   * Der uebersteuerte Satz geht MIT in die Vorschau: Wer 540 eintippt, will
   * sehen, was daraus samt Zuschlaegen wird, und nicht erst nach dem Absenden.
   */
  const holeVorschau = useCallback(
    async (zum: Honorarentwurf, termin: string, bedarf: string) => {
      setzeVorschauLaedt(true);
      try {
        setzeVorschau(
          await ladeVorschau({
            termin,
            bedarf,
            ressource: zum.ressource,
            leistungsart: zum.leistungsart,
            ...(zum.satz.trim() === '' ? {} : { satz: zum.satz.trim() }),
          }),
        );
      } catch (f) {
        setzeVorschau(null);
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeVorschauLaedt(false);
      }
    },
    [],
  );

  /** Den Honorarentwurf zu einem Kandidaten aufschlagen oder zuklappen. */
  async function honorarOeffnen(k: Kandidatenzeile): Promise<void> {
    if (offen === null) return;
    if (entwurf?.ressource === k.ressource) {
      setzeEntwurf(null);
      setzeVorschau(null);
      return;
    }
    /*
     * ========================================================================
     * DIE KATEGORIE KOMMT VOM TERMIN (0188)
     * ========================================================================
     *
     * Seit der Honorareintrag AM FAHRTRAINING hängt, weiß das System, welcher
     * gemeint ist: Der Termin trägt seinen Artikel, und die Kategorie trägt
     * denselben. Die Auswahl steht trotzdem da — nach Stunden abgerechnet wird
     * nach Absprache, und das ist eine Entscheidung des Disponenten.
     *
     * OHNE DIESE VORBELEGUNG wäre die Liste eine Zumutung: achtunddreißig
     * Einträge, aus denen der Richtige zu suchen ist, obwohl er feststeht. Und
     * der erste in der Liste wäre fast immer der falsche.
     */
    const artikel = zeilen.find(
      (z) => z.termin === offen.termin && z.bedarf === offen.bedarf,
    )?.artikel;
    const passend = artikel === undefined
      ? undefined
      : leistungsarten.find((l) => l.artikel === artikel);
    const gewaehlt = (passend ?? leistungsarten[0])?.schluessel ?? '';

    const neu: Honorarentwurf = {
      ressource: k.ressource,
      bezeichnung: k.bezeichnung,
      leistungsart: gewaehlt,
      satz: '',
      grund: '',
      mengen: {},
    };
    setzeEntwurf(neu);
    setzeVorschau(null);
    if (gewaehlt !== '') await holeVorschau(neu, offen.termin, offen.bedarf);
  }

  /**
   * Anfragen — mit oder ohne Honorar.
   *
   * EINE STELLE FUER BEIDE FAELLE, und das ist Absicht: Zwei Knoepfe waeren zwei
   * Wege zu derselben Anfrage, und der zweite waere der, den niemand prueft.
   * Ohne Entwurf geht die Anfrage wie bisher hinaus.
   */
  async function anfragen(k: Kandidatenzeile): Promise<void> {
    if (offen === null) return;
    const mitHonorar = entwurf !== null && entwurf.ressource === k.ressource;
    await handgriff(async () => {
      await einsatzAnfragen({
        termin: offen.termin,
        bedarf: offen.bedarf,
        ressource: k.ressource,
        ...(mitHonorar && entwurf.leistungsart !== ''
          ? {
              honorar: {
                leistungsart: entwurf.leistungsart,
                ...(entwurf.satz.trim() === '' ? {} : { satz: entwurf.satz.trim() }),
                ...(entwurf.grund === '' ? {} : { uebersteuerungsgrund: entwurf.grund }),
                ...(Object.keys(entwurf.mengen).length === 0
                  ? {}
                  : {
                      zuschlaege: Object.entries(entwurf.mengen)
                        .filter(([, menge]) => menge.trim() !== '')
                        .map(([zuschlagsart, menge]) => ({ zuschlagsart, menge: menge.trim() })),
                    }),
              },
            }
          : {}),
      });
      setzeEntwurf(null);
      setzeVorschau(null);
    }, `${k.bezeichnung} ist angefragt; die Frist läuft.`);
  }

  /*
   * DIE AUSWAHL DER WERKZEUGLEISTE — auf dem Geladenen, ohne neue Abfrage.
   * „ohne Ort" ist ein eigener Wert: Ein Termin ohne Ort ist kein Termin an
   * jedem Standort.
   */
  const orte = [...new Set(zeilen.map((z) => z.ort ?? ''))].sort((a, b) => a.localeCompare(b, 'de'));
  const gezeigt = zeilen.filter(
    (z) =>
      (ab === '' || ortstag(z.beginn) >= ab)
      && (ort === '' || (z.ort ?? '') === (ort === '-' ? '' : ort))
      && (ansicht === 'alle'
        || (ansicht === 'verstrichen' && z.ueberfaellig)
        || (ansicht === 'ungefragt' && z.offeneAnfragen === 0)),
  );
  const verstrichen = zeilen.filter((z) => z.ueberfaellig).length;
  const laufend = zeilen.reduce((s, z) => s + z.offeneAnfragen, 0);
  const gewaehlt =
    offen === null
      ? undefined
      : zeilen.find((z) => z.termin === offen.termin && z.bedarf === offen.bedarf);

  const werkzeuge = (
    <>
      <label className="liste__filter">
        Termine ab{' '}
        <input
          type="date"
          aria-label="Termine ab"
          value={ab}
          onChange={(e) => {
            setzeAb(e.target.value);
          }}
        />
      </label>
      <label className="liste__filter">
        Standort{' '}
        <select
          aria-label="Standort"
          value={ort}
          onChange={(e) => {
            setzeOrt(e.target.value);
          }}
        >
          <option value="">Alle Standorte</option>
          {orte.map((o) =>
            o === '' ? (
              <option key="-" value="-">
                ohne Ort
              </option>
            ) : (
              <option key={o} value={o}>
                {o}
              </option>
            ),
          )}
        </select>
      </label>
      {/* RES-50: Die verstrichene Frist ist die Arbeit — sie lässt sich allein zeigen. */}
      <Segmente
        beschriftung="Ansicht"
        gewaehlt={ansicht}
        beiWahl={setzeAnsicht}
        optionen={[
          { wert: 'alle', text: 'Alle' },
          { wert: 'verstrichen', text: 'Frist verstrichen' },
          { wert: 'ungefragt', text: 'Ohne Anfrage' },
        ]}
      />
    </>
  );

  const seite =
    zeilen.length === 0 ? undefined : (
      <>
        <section className="seitenblock" aria-label="Stand der Besetzung">
          <div className="seitenblock__kopf">
            <h2 className="seitenblock__titel">Stand der Besetzung</h2>
          </div>
          {/* RES-50: Nach der Frist fragt der Lauf nicht mehr — dann entscheidet ein Mensch. */}
          {verstrichen === 0 ? (
            <div className="streifen streifen--gut">Bei keinem Bedarf ist die Frist verstrichen.</div>
          ) : (
            <div className="streifen streifen--warn">
              <strong>
                {verstrichen === 1
                  ? 'Bei 1 Bedarf ist die Frist verstrichen'
                  : `Bei ${String(verstrichen)} Bedarfen ist die Frist verstrichen`}
              </strong>{' '}
              — der Lauf fragt dort nicht mehr. Besetzen Sie selbst oder entscheiden Sie über
              Verlegung oder Absage.
            </div>
          )}
          <p className="hilfe">
            {zeilen.length} offene {zeilen.length === 1 ? 'Besetzung' : 'Besetzungen'} ·{' '}
            {laufend} {laufend === 1 ? 'Anfrage läuft' : 'Anfragen laufen'}
          </p>
        </section>

        {gewaehlt !== undefined && (
          <section className="seitenblock" aria-label="Gewählter Bedarf">
            <div className="seitenblock__kopf">
              <h2 className="seitenblock__titel">Gewählter Bedarf</h2>
            </div>
            <Feldraster>
              <Feldzeile beschriftung="Termin" wert={gewaehlt.artikelname} />
              <Feldzeile beschriftung="Beginn" wert={zeitpunkt(gewaehlt.beginn)} />
              <Feldzeile beschriftung="Ort" wert={gewaehlt.ort} />
              <Feldzeile
                beschriftung="Gesucht"
                wert={gewaehlt.bedarfsname}
                {...(gewaehlt.luecke > 1
                  ? { anzeige: `${gewaehlt.bedarfsname} (${String(gewaehlt.luecke)} fehlen)` }
                  : {})}
              />
              <Feldzeile
                beschriftung="Besetzungsfrist"
                wert={tag(gewaehlt.fristAm)}
                anzeige={
                  gewaehlt.ueberfaellig ? (
                    <span className="marke marke--warn">
                      {tag(gewaehlt.fristAm)} · verstrichen
                    </span>
                  ) : (
                    `${tag(gewaehlt.fristAm)} · ${frist(gewaehlt)}`
                  )
                }
              />
            </Feldraster>
          </section>
        )}

        {offen !== null && (
          <>
        {/* ==========================================================
            DAS HONORAR ZUR ANFRAGE (RES-43, RES-70, RES-71)
            ==========================================================

            Auftraggeber am 11.09.2026: die Honorartabelle „inkl.
            anpassungsmöglichkeit bei der anfrage". Das ist diese Karte.

            Sie zeigt, WAS die Tabelle sagt (Satz und Stufe, RES-69), und
            daneben das Feld, mit dem man davon abweicht — samt Grund, den
            RES-70 verlangt. Die Zuschläge, die das System selbst
            entscheiden kann, stehen als gesetzt da; die anderen warten auf
            eine Menge.

            GESCHRIEBEN WIRD NICHTS, bis jemand anfragt. */}
        {darfHonorar && entwurf !== null && (
          <section className="seitenblock" aria-label={`Honorar für ${entwurf.bezeichnung}`}>
            <div className="seitenblock__kopf">
              <h3 className="seitenblock__titel">Honorar für {entwurf.bezeichnung}</h3>
            </div>
            <p className="hilfe">
              Netto. Alles hier gilt für diesen einen Einsatz; die Tabelle bleibt
              unberührt.
            </p>

            <div className="feldreihe">
              <div className="feld">
                <label htmlFor="hon-leistung">Leistung</label>
                <select
                  id="hon-leistung"
                  value={entwurf.leistungsart}
                  onChange={(e) => {
                    const geaendert = { ...entwurf, leistungsart: e.target.value };
                    setzeEntwurf(geaendert);
                    void holeVorschau(geaendert, offen.termin, offen.bedarf);
                  }}
                >
                  {leistungsarten.map((l) => (
                    <option key={l.schluessel} value={l.schluessel}>
                      {l.bezeichnung} ({EINHEITSNAME[l.einheit]})
                    </option>
                  ))}
                </select>
              </div>

              <div className="feld">
                <label htmlFor="hon-satz">Abweichender Satz</label>
                <input
                  id="hon-satz"
                  className="feld__eingabe feld__eingabe--schmal"
                  inputMode="decimal"
                  value={entwurf.satz}
                  placeholder={vorschau?.tabellensatz ?? 'kein Tabellensatz'}
                  onChange={(e) => {
                    setzeEntwurf({ ...entwurf, satz: e.target.value });
                  }}
                  onBlur={() => {
                    void holeVorschau(entwurf, offen.termin, offen.bedarf);
                  }}
                />
                <span className="feld__hilfe">
                  Leer lassen heißt: der Tabellensatz gilt.
                </span>
              </div>

              {/*
                DER GRUND ERSCHEINT, WENN ER GEBRAUCHT WIRD (RES-70).

                Abweichend vereinbart ODER gar kein Tabellensatz — in
                beiden Fällen verlangt der Server einen Grund, und die
                Datenbank ebenfalls. Ein Feld, das immer dasteht, wäre
                eine Pflicht, die meistens keine ist.
              */}
              {(entwurf.satz.trim() !== '' || vorschau?.ohneTabellensatz === true) && (
                <div className="feld">
                  <label htmlFor="hon-grund">Grund der Abweichung</label>
                  <select
                    id="hon-grund"
                    value={entwurf.grund}
                    onChange={(e) => {
                      setzeEntwurf({ ...entwurf, grund: e.target.value });
                    }}
                  >
                    <option value="">— bitte wählen —</option>
                    {gruende.map((g) => (
                      <option key={g.schluessel} value={g.schluessel}>
                        {g.bezeichnung}
                      </option>
                    ))}
                  </select>
                  <span className="feld__hilfe">
                    Pflicht bei jeder Abweichung vom Tabellenwert — auch, wenn gar
                    keiner hinterlegt ist.
                  </span>
                </div>
              )}
            </div>

            {vorschauLaedt && <p role="status">Wird gerechnet …</p>}

            {vorschau !== null && (
              <>
                <p className="hinweiszeile">
                  {vorschau.tabellensatz === null
                    ? `Für ${vorschau.leistungsartName} zum ${TARIFNAME[vorschau.tarif]} `
                      + 'ist kein Satz hinterlegt.'
                    : `Tabellensatz ${alsEurosatz(vorschau.tabellensatz)} — `
                      + `${TARIFNAME[vorschau.tarif]}, ${STUFENNAME[vorschau.stufe]}.`}
                </p>

                {/*
                  DER ERSATZWEISE TARIF MUSS DASTEHEN (0186).

                  Ein Einsatz am Samstag, für den es keinen Wochenendtarif
                  gibt, wird zum Wochentagssatz gerechnet — plus
                  Wochenendzuschlag, wenn einer hinterlegt ist. Beides
                  zusammen ist meistens weniger als ein Wochenendtarif, und
                  das soll der Disponent sehen, BEVOR er anfragt. Sonst
                  merkt es zuerst der Trainer, wenn seine Rechnung nicht
                  passt.
                */}
                {vorschau.ersatzweise && (
                  <p className="hinweiszeile">
                    <span className="marke marke--warn">Kein Wochenendtarif</span> Für{' '}
                    {vorschau.leistungsartName} ist keiner hinterlegt; gerechnet wird mit
                    dem Wochentagstarif. Ein Wochenendzuschlag kommt dazu, wenn einer
                    hinterlegt ist.
                  </p>
                )}

                <div className="tabellenhuelle tabellenhuelle--telefon">
                  <table className="liste__tabelle" aria-label="Honorarvorschau">
                    <thead>
                      <tr>
                        <th scope="col">Posten</th>
                        <th scope="col">Satz</th>
                        <th scope="col">Menge</th>
                        <th scope="col">Betrag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vorschau.positionen.map((pos) => (
                        <tr
                          key={`${pos.art}-${pos.schluessel}`}
                          className={pos.zutreffend === 'offen' ? 'liste__zeile--blass' : undefined}
                        >
                          <th scope="row">
                            {pos.bezeichnung}{' '}
                            {pos.zutreffend === 'offen' && (
                              <span className="marke marke--warn">zu bestätigen</span>
                            )}
                            <br />
                            <span className="hilfe">{pos.begruendung}</span>
                          </th>
                          <td className="zahl">{pos.satz}</td>
                          <td>
                            {pos.zutreffend === 'offen' ? (
                              /*
                                Die Menge trägt der Mensch nach: Wie weit
                                jemand gefahren ist und ob er übernachtet
                                hat, weiß nur er. Leer heißt: kommt nicht
                                dazu.
                              */
                              <input
                                className="feld__eingabe feld__eingabe--schmal"
                                inputMode="decimal"
                                aria-label={`Menge für ${pos.bezeichnung}`}
                                value={entwurf.mengen[pos.schluessel] ?? ''}
                                onChange={(e) => {
                                  setzeEntwurf({
                                    ...entwurf,
                                    mengen: {
                                      ...entwurf.mengen,
                                      [pos.schluessel]: e.target.value,
                                    },
                                  });
                                }}
                              />
                            ) : (
                              <span className="zahl">{pos.menge}</span>
                            )}
                          </td>
                          <td className="zahl">
                            {pos.zutreffend === 'offen' ? (
                              <span className="hilfe">noch nicht gerechnet</span>
                            ) : (
                              alsEuro(pos.betrag)
                            )}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <th scope="row">Summe (netto)</th>
                        <td />
                        <td />
                        <td className="zahl">{alsEuro(vorschau.summe)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {vorschau.befund !== 'ja' && vorschau.befund !== 'nicht_extern' && (
                  <p className="hinweiszeile">
                    {BEFUNDNAME[vorschau.befund]}. Die Anfrage bleibt möglich — eine
                    Anfrage ist keine Beauftragung. Die Beauftragung braucht
                    beides.
                  </p>
                )}
              </>
            )}

            {darfZuordnen && (
              <div className="knopfreihe">
                <button
                  type="button"
                  className="knopf knopf--haupt"
                  disabled={
                    kandidatenLaden
                    || vorschauLaedt
                    /* Kein Grund, keine Abweichung — der Server wiese es ab. */
                    || ((entwurf.satz.trim() !== ''
                      || vorschau?.ohneTabellensatz === true)
                      && entwurf.grund === '')
                  }
                  onClick={() => {
                    const k = kandidaten.find((z) => z.ressource === entwurf.ressource);
                    if (k !== undefined) void anfragen(k);
                  }}
                >
                  Mit diesem Honorar anfragen
                </button>
              </div>
            )}
          </section>
        )}
          </>
        )}
      </>
    );

  /*
   * DIE RÜCKFRAGE ZUR ANTWORT (CRM-104, RES-33, RES-35). Eingetragen wird,
   * was der Trainer am Telefon gesagt hat — und eine beantwortete Anfrage
   * bleibt, wie sie ist. Der Termin steht dabei, damit niemand die Zeile
   * darüber erwischt.
   */
  const angefragt =
    offen === null
      ? undefined
      : zeilen.find((z) => z.termin === offen.termin && z.bedarf === offen.bedarf);
  const antwortFenster =
    nachfrage === null ? null : (
      <Rueckfrage
        frage={nachfrage.zusage ? 'Zusage eintragen?' : 'Absage eintragen?'}
        unterzeile={[
          nachfrage.anfrage.bezeichnung,
          ...(angefragt === undefined
            ? []
            : [angefragt.artikelname, zeitpunkt(angefragt.beginn)]),
        ].join(' · ')}
        tat={nachfrage.zusage ? 'Zusage eintragen' : 'Absage eintragen'}
        beiBestaetigen={() => antwortEintragen(nachfrage.zusage, nachfrage.anfrage)}
        beiSchliessen={() => {
          setzeNachfrage(null);
        }}
      >
        {nachfrage.zusage ? (
          <p>
            {nachfrage.anfrage.bezeichnung} übernimmt den Einsatz
            {angefragt === undefined ? '' : ` als ${angefragt.bedarfsname}`}. Die Anfrage ist
            damit beantwortet und lässt sich hier nicht mehr ändern.
          </p>
        ) : (
          <>
            <p>Grund: „{absagegrundVon(nachfrage.anfrage.belegung).trim()}“</p>
            <p>
              Die Anfrage ist damit beantwortet und lässt sich hier nicht mehr ändern; danach
              wird der Nächste gefragt.
            </p>
          </>
        )}
      </Rueckfrage>
    );

  return (
    <Planung
      werkzeuge={werkzeuge}
      seitenBeschriftung="Stand und Auswahl"
      {...(seite === undefined ? {} : { seite })}
      oben={
        <>
          {fehler !== null && <Meldung fehler={fehler} />}
          {meldung !== null && (
            <p className="hinweiszeile" role="status">
              {meldung}
            </p>
          )}
          <p className="hinweiszeile">
            Termine werden geplant, veröffentlicht und gebucht, bevor eine Zusage vorliegt.
            Das System fragt von sich aus an
            {regel === null
              ? '.'
              : ` — ${String(regel.versuche)} Versuche mit je ${String(regel.fristStunden)} Stunden.`}{' '}
            <strong>Abgesagt wird nichts von selbst</strong>.
          </p>
          {laedt ? (
            <p role="status">Wird geladen …</p>
          ) : zeilen.length === 0 ? (
            <p className="hinweiszeile">
              Jeder künftige Termin ist besetzt. Diese Liste füllt sich von selbst, sobald
              ein Termin angelegt wird, dessen Trainerbedarf noch offen ist.
            </p>
          ) : gezeigt.length === 0 ? (
            <p className="hinweiszeile">Keine offene Besetzung passt zur Auswahl.</p>
          ) : undefined}
        </>
      }
    >
      {!laedt && gezeigt.length > 0 && (
        <div className="karte karte--tabelle">
          <table className="liste__tabelle" aria-label="Offene Besetzungen">
            <thead>
              <tr>
                <th scope="col">Termin</th>
                <th scope="col">Ort</th>
                <th scope="col">Gesucht</th>
                <th scope="col">Stand</th>
                <th scope="col">Frist</th>
                <th scope="col">Handlung</th>
              </tr>
            </thead>
            <tbody>
              {gezeigt.map((z) => {
                const auf = offen?.termin === z.termin && offen.bedarf === z.bedarf;
                return (
                  <tr key={`${z.termin}:${z.bedarf}`} className={auf ? 'liste__zeile--gewaehlt' : undefined}>
                    <th scope="row">
                      <span className="liste__name">{z.artikelname}</span>
                      <br />
                      <span className="hilfe">{zeitpunkt(z.beginn)}</span>
                    </th>
                    <td>{z.ort ?? <span className="hilfe">ohne Ort</span>}</td>
                    <td>
                      {z.bedarfsname}
                      {z.luecke > 1 && (
                        <span className="hilfe"> ({String(z.luecke)} fehlen)</span>
                      )}
                    </td>
                    <td>
                      {/*
                        DREI ZUSTÄNDE, DIE VERSCHIEDENES HEISSEN (RES-33). Eine
                        laufende Anfrage ist Arbeit, die gerade woanders liegt —
                        da ist nichts zu tun, sie trägt die Marke des laufenden
                        Zustands. „Noch niemand gefragt" ist der Zustand, in dem
                        der nächste Lauf tätig wird. Und wenn die Frist vorbei
                        ist, fragt der Lauf gar nicht mehr (RES-50): Dann ist es
                        eine Aufgabe und keine Wartezeit — in der Achtungsfarbe.
                      */}
                      {z.offeneAnfragen > 0 ? (
                        <span className={markenklasse('laufend')}>
                          {z.offeneAnfragen === 1
                            ? 'Anfrage läuft'
                            : `${String(z.offeneAnfragen)} Anfragen laufen`}
                        </span>
                      ) : z.ueberfaellig ? (
                        <span className="marke marke--warn">niemand gefragt</span>
                      ) : (
                        <span className="hilfe">noch niemand gefragt</span>
                      )}
                    </td>
                    <td>
                      {/* RES-49: die Besetzungsfrist — verstrichen in der Achtungsfarbe (ÄA-41). */}
                      {z.ueberfaellig ? (
                        <span className="marke marke--warn">{frist(z)}</span>
                      ) : (
                        frist(z)
                      )}
                      <br />
                      <span className="hilfe liste__einzeilig">bis {tag(z.fristAm)}</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--verweis"
                        aria-expanded={auf}
                        onClick={() => void aufklappen(z)}
                      >
                        {auf ? 'Schließen' : 'Wer kann?'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {offen !== null && (
        <div className="karte">
          <div className="karte__titel">
            <h2>Wer diesen Bedarf decken kann</h2>
            <small>
              In der Liste steht nur, wer den Bedarf deckt und im Zeitraum frei ist.
              Wer gerade angefragt ist, steht darunter — er blockiert sich
              durch seine eigene Anfrage.
            </small>
          </div>

          {kandidatenLaden ? (
            <p role="status">Wird geladen …</p>
          ) : (
            <>
            {/* RES-50: die drei Wege, die bleiben, wenn niemand frei ist. */}
            {kandidaten.length === 0 ? (
              <p className="hinweiszeile">
                Niemand ist im Zeitraum frei und deckt den Bedarf. Es bleiben die drei
                anderen Wege: interne Besetzung, Verlegung des Termins oder Absage — alle
                drei außerhalb dieser Maske.
              </p>
            ) : (
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle" aria-label="Kandidaten">
                  <thead>
                    <tr>
                      <th scope="col">Ressource</th>
                      <th scope="col">Standort</th>
                      <th scope="col">Stand</th>
                      <th scope="col">Vertragliche Grundlage</th>
                      {darfZuordnen && <th scope="col">Handlung</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {kandidaten.map((k) => (
                      <tr key={k.ressource}>
                        <th scope="row">
                          {k.kennung} — {k.bezeichnung}
                        </th>
                        <td>{k.standort ?? <span className="hilfe">ohne Standort</span>}</td>
                        <td>
                          {k.schonGefragt && k.letzterStatus !== null ? (
                            <span className="marke marke--inaktiv">
                              schon gefragt: {STAND[k.letzterStatus] ?? k.letzterStatus}
                            </span>
                          ) : (
                            <span className="hilfe">noch nicht gefragt</span>
                          )}
                          {k.qualifikation !== null && (
                            <>
                              {' '}
                              <span className="marke marke--warn">
                                {BEFUND[k.qualifikation] ?? k.qualifikation}
                              </span>
                            </>
                          )}
                        </td>
                        {/*
                          RES-64: Steht die vertragliche Grundlage? Eine ANZEIGE
                          und keine Sperre — eine Anfrage ist keine Beauftragung,
                          und die Beauftragung nach RES-60 ist nicht gebaut. Der
                          Disponent soll nur sehen, wen er da gerade fragt.
                        */}
                        <td>
                          {k.beauftragung === 'ja' ? (
                            <span className="marke marke--gut">liegt vor</span>
                          ) : k.beauftragung === 'nicht_extern' ? (
                            <span className="hilfe">{BEFUNDNAME.nicht_extern}</span>
                          ) : (
                            <span className="marke marke--warn">
                              {BEFUNDNAME[k.beauftragung] ?? k.beauftragung}
                            </span>
                          )}
                        </td>
                        {darfZuordnen && (
                          <td>
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              /*
                                TAG-14: Wer die geforderte Qualifikation gar
                                nicht oder nicht mehr trägt, wird nicht
                                gefragt. Die Zuteilung wiese ihn danach ab, und
                                die Anfrage wäre eine Frist, die niemandem
                                nutzt.
                              */
                              disabled={
                                k.qualifikation === 'fehlt' || k.qualifikation === 'abgelaufen'
                              }
                              onClick={() => void anfragen(k)}
                            >
                              Anfragen
                            </button>
                            {/*
                              DAS HONORAR IST EIN ZWEITER KNOPF UND KEIN
                              PFLICHTSCHRITT (RES-70).
  
                              Wer nur besetzen will, klickt „Anfragen" wie
                              bisher. Wer das Honorar festlegen oder anpassen
                              will, schlägt es auf — und dann geht es MIT der
                              Anfrage hinaus. Ein Pflichtfeld wäre hier falsch:
                              Die Honorartabelle ist am Tag der Auslieferung
                              leer, und die Besetzung darf nicht darauf warten.
                            */}
                            {darfHonorar && leistungsarten.length > 0 && (
                              <>
                                {' '}
                                <button
                                  type="button"
                                  className="knopf knopf--klein"
                                  onClick={() => void honorarOeffnen(k)}
                                >
                                  {entwurf?.ressource === k.ressource
                                    ? 'Honorar schließen'
                                    : 'Honorar'}
                                </button>
                              </>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {verlauf.length > 0 && (
              <>
                <h3 className="karte__unterteil">Wen wir schon gefragt haben</h3>
                <div className="tabellenhuelle tabellenhuelle--telefon">
                  <table className="liste__tabelle" aria-label="Bisherige Anfragen">
                    <thead>
                      <tr>
                        <th scope="col">Versuch</th>
                        <th scope="col">Ressource</th>
                        <th scope="col">Stand</th>
                        <th scope="col">Frist</th>
                        {darfZuordnen && <th scope="col">Antwort eintragen</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {verlauf.map((v) => (
                        <tr key={v.belegung}>
                          <td className="zahl">
                            {v.versuch === null ? (
                              <span className="hilfe">von Hand</span>
                            ) : (
                              v.versuch
                            )}
                          </td>
                          <th scope="row">
                            {v.kennung} — {v.bezeichnung}
                          </th>
                          <td>
                            {v.status === 'vorlaeufig' ? (
                              <span className="marke marke--warn">
                                {STAND[v.status] ?? v.status}
                              </span>
                            ) : v.status === 'zugesagt' || v.status === 'bestaetigt' ? (
                              <span className="marke marke--gut">
                                {STAND[v.status] ?? v.status}
                              </span>
                            ) : (
                              <span className="marke marke--inaktiv">
                                {STAND[v.status] ?? v.status}
                              </span>
                            )}
                            {v.absagegrund !== null && (
                              <>
                                <br />
                                <span className="hilfe">{v.absagegrund}</span>
                              </>
                            )}
                          </td>
                          <td className="liste__einzeilig">
                            {v.verfaelltAm === null ? (
                              <span className="hilfe">—</span>
                            ) : (
                              zeitpunkt(v.verfaelltAm)
                            )}
                          </td>
                          {darfZuordnen && (
                            <td>
                              {/*
                                NUR AUF EINE LAUFENDE ANFRAGE. Eine
                                beantwortete Anfrage bleibt, wie sie ist
                                (RES-35) — der Dienstleisterzugang wird
                                dieselbe Antwort später selbst geben (RES-30),
                                hier trägt sie ein, wer den Anruf entgegennimmt.
                              */}
                              {v.status === 'vorlaeufig' ? (
                                <div className="liste__leiste">
                                  <button
                                    type="button"
                                    className="knopf knopf--klein"
                                    onClick={() => {
                                      setzeNachfrage({ zusage: true, anfrage: v });
                                    }}
                                  >
                                    Zusage …
                                  </button>{' '}
                                  <label className="liste__filter">
                                    <span className="nur-vorlesen">
                                      Grund der Absage von {v.bezeichnung}
                                    </span>
                                    <input
                                      value={absagegrundVon(v.belegung)}
                                      placeholder="Grund der Absage"
                                      onChange={(e) => {
                                        const wert = e.target.value;
                                        setzeAbsagegruende((g) => ({ ...g, [v.belegung]: wert }));
                                      }}
                                    />
                                  </label>{' '}
                                  {/*
                                    CRM-104: Der Grund bleibt hier im Formular, die
                                    Rückfrage zeigt ihn nur — er steht nicht zweimal.
                                  */}
                                  <button
                                    type="button"
                                    className="knopf knopf--eingriff knopf--klein"
                                    /* RES-33: Ohne Grund hilft die Absage beim nächsten Versuch nicht. */
                                    disabled={absagegrundVon(v.belegung).trim() === ''}
                                    onClick={() => {
                                      setzeNachfrage({ zusage: false, anfrage: v });
                                    }}
                                  >
                                    Absage …
                                  </button>
                                </div>
                              ) : (
                                <span className="hilfe">beantwortet</span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            </>
          )}
        </div>
      )}
      {antwortFenster}
    </Planung>
  );
}
